import { ArrowUpRight, X } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useEndpoint } from '../hooks/useEndpoints';
import { endpointLabel } from '../utils/endpointLabel';
import { ClientTrafficSection } from './traffic/ClientTrafficSection';
import { LinkTrafficSection } from './traffic/LinkTrafficSection';
import type { TrafficRange } from './traffic/range';

interface EndpointLinkPanelProps {
  endpointId: string;
  /** One of the endpoint's `links[].linkId`. */
  linkId: string;
  trafficRange?: TrafficRange;
  onClose?: () => void;
  onEndpointSelect?: (endpointId: string) => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr>
      <th className="text-left text-xs font-medium text-muted-foreground py-1.5 w-2/5">{label}</th>
      <td className="py-1.5">{children}</td>
    </tr>
  );
}

/**
 * An endpoint's attachment, seen from the device it is attached to: the switch port's traffic for
 * a wired endpoint — the endpoint itself is not monitored — or the AP's view of a wireless one.
 * Resolved from the endpoint's current links, so a URL to an attachment that has since moved says
 * so rather than showing the old port.
 */
export default function EndpointLinkPanel({ endpointId, linkId, trafficRange, onClose, onEndpointSelect }: EndpointLinkPanelProps) {
  const { data: endpoint, isLoading } = useEndpoint(endpointId);
  const link = endpoint?.links?.find((l) => l.linkId === linkId);
  const close = onClose ? (
    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClose} aria-label="Close">
      <X className="w-3.5 h-3.5" />
    </Button>
  ) : null;
  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Loading link…</p>
      </div>
    );
  }
  if (!endpoint || !link) {
    return (
      <div className="rounded-md border border-border bg-card p-4 flex items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {endpoint ? 'The endpoint is no longer attached here.' : 'Endpoint not found'}
        </p>
        {close}
      </div>
    );
  }
  const deviceName = link.deviceName ?? link.deviceId.slice(0, 8);
  const where = link.kind === 'wired' && link.interfaceName ? `${deviceName}:${link.interfaceName}` : deviceName;
  return (
    <div className="rounded-md border border-border bg-card p-4 endpoint-link-panel">
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold truncate">
            {endpointLabel(endpoint)} → {where}
          </h3>
          {onEndpointSelect ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-sev-5 hover:underline"
              onClick={() => { onEndpointSelect(endpoint.id); }}
            >
              Endpoint details
            </button>
          ) : null}
        </div>
        {close}
      </div>
      <table className="w-full text-sm mb-4">
        <tbody className="divide-y divide-border-subtle">
          <Row label={link.kind === 'wired' ? 'Switch' : 'Access point'}>
            <Link to={`/devices/${link.deviceId}`} className="inline-flex items-center gap-1 hover:underline">
              {deviceName}
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </Row>
          {link.kind === 'wired' ? (
            <Row label="Port">
              <code className="font-mono text-xs">{link.interfaceName ?? '—'}</code>
            </Row>
          ) : (
            <>
              <Row label="SSID">{link.ssid ?? '—'}</Row>
              <Row label="Radio">
                {[link.band, link.channel !== null ? `channel ${String(link.channel)}` : null].filter(Boolean).join(', ') || '—'}
              </Row>
              <Row label="Signal">{link.signalDbm !== null ? `${String(link.signalDbm)} dBm` : '—'}</Row>
            </>
          )}
          <Row label="VLAN">{link.vlanId !== null ? String(link.vlanId) : '—'}</Row>
          {link.behindDeviceName ? (
            <Row label="Seen through">
              <span className="text-muted-foreground">behind {link.behindDeviceName}</span>
            </Row>
          ) : null}
        </tbody>
      </table>
      {link.kind === 'wired' ? (
        <LinkTrafficSection connectionId={link.linkId} range={trafficRange} />
      ) : (
        <ClientTrafficSection endpointId={endpoint.id} range={trafficRange} />
      )}
    </div>
  );
}
